import React, { useState, useEffect } from 'react'
import { X, Plus, Minus, Check, AlertCircle, Package, Zap, Search, Filter, Users, ArrowRight, CheckCircle, XCircle } from 'lucide-react'
import { adminApi } from '../services/api'
import toast from 'react-hot-toast'

const BulkFeatureAssignmentModal = ({ tenants, isOpen, onClose, onComplete }) => {
  const [features, setFeatures] = useState([])
  const [selectedFeatures, setSelectedFeatures] = useState([])
  const [operation, setOperation] = useState('assign') // 'assign' or 'unassign'
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [results, setResults] = useState(null)
  const [step, setStep] = useState(1) // 1: Select Features, 2: Confirm, 3: Results

  // Category colors and icons
  const categoryConfig = {
    core: { color: 'bg-blue-100 text-blue-800', icon: Package },
    advanced: { color: 'bg-purple-100 text-purple-800', icon: Zap },
    reporting: { color: 'bg-green-100 text-green-800', icon: '📊' },
    integration: { color: 'bg-orange-100 text-orange-800', icon: '🔗' },
    security: { color: 'bg-red-100 text-red-800', icon: '🔒' }
  }

  const planColors = {
    free: 'bg-gray-100 text-gray-800',
    basic: 'bg-blue-100 text-blue-800',
    premium: 'bg-purple-100 text-purple-800',
    enterprise: 'bg-gold-100 text-gold-800'
  }

  useEffect(() => {
    if (isOpen) {
      fetchFeatures()
      setStep(1)
      setResults(null)
      setSelectedFeatures([])
    }
  }, [isOpen])

  const fetchFeatures = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getFeatures()
      setFeatures(response.data.features || [])
    } catch (error) {
      toast.error('Failed to fetch features')
      console.error('Error fetching features:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    )
  }

  const handleSelectAll = () => {
    const filteredFeatures = getFilteredFeatures()
    const allSelected = filteredFeatures.every(feature => selectedFeatures.includes(feature.id))
    
    if (allSelected) {
      setSelectedFeatures(prev => prev.filter(id => !filteredFeatures.some(f => f.id === id)))
    } else {
      setSelectedFeatures(prev => [...new Set([...prev, ...filteredFeatures.map(f => f.id)])])
    }
  }

  const getFilteredFeatures = () => {
    return features.filter(feature => {
      const matchesSearch = feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          feature.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = categoryFilter === 'all' || feature.category === categoryFilter
      return matchesSearch && matchesCategory
    })
  }

  const handleBulkOperation = async () => {
    if (selectedFeatures.length === 0) {
      toast.error('Please select at least one feature')
      return
    }

    try {
      setProcessing(true)
      const tenantIds = tenants.map(t => t.id)
      
      let response
      if (operation === 'assign') {
        response = await adminApi.bulkAssignFeatures(tenantIds, selectedFeatures, true)
      } else {
        response = await adminApi.bulkUnassignFeatures(tenantIds, selectedFeatures)
      }

      setResults(response.data)
      setStep(3)
      
      toast.success(response.data.message)
      
      if (onComplete) {
        onComplete()
      }
    } catch (error) {
      toast.error(`Failed to ${operation} features`)
      console.error(`Error ${operation}ing features:`, error)
    } finally {
      setProcessing(false)
    }
  }

  const getCompatibilityWarnings = () => {
    const warnings = []
    const selectedFeatureObjects = features.filter(f => selectedFeatures.includes(f.id))
    
    tenants.forEach(tenant => {
      const incompatibleFeatures = selectedFeatureObjects.filter(feature => {
        const plans = ['free', 'basic', 'premium', 'enterprise']
        const tenantPlanIndex = plans.indexOf(tenant.subscriptionPlan)
        const requiredPlanIndex = plans.indexOf(feature.requiredPlan)
        return requiredPlanIndex > tenantPlanIndex
      })
      
      if (incompatibleFeatures.length > 0) {
        warnings.push({
          tenant,
          incompatibleFeatures
        })
      }
    })
    
    return warnings
  }

  const filteredFeatures = getFilteredFeatures()
  const compatibilityWarnings = getCompatibilityWarnings()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Bulk Feature {operation === 'assign' ? 'Assignment' : 'Unassignment'}
              </h3>
              <p className="text-sm text-gray-600">
                {operation === 'assign' ? 'Assign' : 'Unassign'} features to {tenants.length} tenant{tenants.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span>Select Features</span>
            </div>
            <ArrowRight size={16} className="text-gray-400" />
            <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span>Confirm</span>
            </div>
            <ArrowRight size={16} className="text-gray-400" />
            <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span>Results</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: '60vh' }}>
          {step === 1 && (
            <div className="space-y-6">
              {/* Operation Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Operation Type
                </label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setOperation('assign')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                      operation === 'assign' 
                        ? 'bg-blue-50 border-blue-200 text-blue-700' 
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Plus size={16} />
                    <span>Assign Features</span>
                  </button>
                  <button
                    onClick={() => setOperation('unassign')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                      operation === 'unassign' 
                        ? 'bg-red-50 border-red-200 text-red-700' 
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Minus size={16} />
                    <span>Unassign Features</span>
                  </button>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Features
                  </label>
                  <div className="relative">
                    <Search size={20} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search features..."
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Category
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="core">Core</option>
                    <option value="advanced">Advanced</option>
                    <option value="reporting">Reporting</option>
                    <option value="integration">Integration</option>
                    <option value="security">Security</option>
                  </select>
                </div>
              </div>

              {/* Bulk Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {filteredFeatures.every(f => selectedFeatures.includes(f.id)) ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedFeatures.length} of {filteredFeatures.length} features selected
                  </span>
                </div>
              </div>

              {/* Features List */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading features...</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredFeatures.map((feature) => {
                        const isSelected = selectedFeatures.includes(feature.id)
                        const categoryInfo = categoryConfig[feature.category] || { color: 'bg-gray-100 text-gray-800', icon: Package }
                        
                        return (
                          <div
                            key={feature.id}
                            className={`p-4 hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                            onClick={() => handleFeatureToggle(feature.id)}
                          >
                            <div className="flex items-start space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleFeatureToggle(feature.id)}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <h4 className="text-sm font-medium text-gray-900">{feature.name}</h4>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryInfo.color}`}>
                                      {feature.category}
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${planColors[feature.requiredPlan]}`}>
                                      {feature.requiredPlan}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{feature.description}</p>
                                {feature.dependencies && feature.dependencies.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500">
                                      Dependencies: {feature.dependencies.join(', ')}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              {filteredFeatures.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No features found</p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Confirmation Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Operation Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Operation: 
                      <span className="font-medium ml-1">
                        {operation === 'assign' ? 'Assign Features' : 'Unassign Features'}
                      </span>
                    </p>
                    <p className="text-gray-600">Tenants: 
                      <span className="font-medium ml-1">{tenants.length}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Features: 
                      <span className="font-medium ml-1">{selectedFeatures.length}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Tenants List */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Affected Tenants</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-40 overflow-y-auto">
                    {tenants.map((tenant) => (
                      <div key={tenant.id} className="p-3 border-b border-gray-200 last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{tenant.name}</p>
                            <p className="text-sm text-gray-500">{tenant.subdomain}</p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${planColors[tenant.subscriptionPlan]}`}>
                            {tenant.subscriptionPlan}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Selected Features */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Selected Features</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-40 overflow-y-auto">
                    {features.filter(f => selectedFeatures.includes(f.id)).map((feature) => {
                      const categoryInfo = categoryConfig[feature.category] || { color: 'bg-gray-100 text-gray-800', icon: Package }
                      
                      return (
                        <div key={feature.id} className="p-3 border-b border-gray-200 last:border-b-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{feature.name}</p>
                              <p className="text-sm text-gray-500">{feature.description}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryInfo.color}`}>
                                {feature.category}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${planColors[feature.requiredPlan]}`}>
                                {feature.requiredPlan}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Compatibility Warnings */}
              {compatibilityWarnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Compatibility Warnings</h4>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p className="mb-2">Some features may not be compatible with certain tenants:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {compatibilityWarnings.map((warning, index) => (
                            <li key={index}>
                              <span className="font-medium">{warning.tenant.name}</span> ({warning.tenant.subscriptionPlan}) - {warning.incompatibleFeatures.map(f => f.name).join(', ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && results && (
            <div className="space-y-6">
              {/* Results Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Operation Results</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-gray-600">Successful: 
                      <span className="font-medium ml-1 text-green-600">{results.summary.totalSuccess}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-gray-600">Errors: 
                      <span className="font-medium ml-1 text-red-600">{results.summary.totalErrors}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-600">Tenants: 
                      <span className="font-medium ml-1">{results.summary.totalTenants}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Detailed Results */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Detailed Results</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    {results.results.map((result) => (
                      <div key={result.tenantId} className="p-4 border-b border-gray-200 last:border-b-0">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900">{result.tenantName}</h5>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-green-600">{result.success.length} success</span>
                            {result.errors.length > 0 && (
                              <span className="text-sm text-red-600">{result.errors.length} errors</span>
                            )}
                          </div>
                        </div>
                        
                        {result.success.length > 0 && (
                          <div className="mb-2">
                            <p className="text-sm text-gray-600 mb-1">Successful operations:</p>
                            <ul className="text-sm text-green-600 space-y-1">
                              {result.success.map((success, index) => (
                                <li key={index} className="flex items-center space-x-2">
                                  <CheckCircle className="h-3 w-3" />
                                  <span>{success.featureName} - {success.action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {result.errors.length > 0 && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Errors:</p>
                            <ul className="text-sm text-red-600 space-y-1">
                              {result.errors.map((error, index) => (
                                <li key={index} className="flex items-center space-x-2">
                                  <XCircle className="h-3 w-3" />
                                  <span>{error.message}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div>
            {step === 1 && (
              <span className="text-sm text-gray-600">
                Step 1 of 3: Select features to {operation}
              </span>
            )}
            {step === 2 && (
              <span className="text-sm text-gray-600">
                Step 2 of 3: Confirm operation
              </span>
            )}
            {step === 3 && (
              <span className="text-sm text-gray-600">
                Step 3 of 3: Review results
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            {step === 1 && (
              <>
                <button
                  onClick={onClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={selectedFeatures.length === 0}
                  className="btn btn-primary disabled:opacity-50"
                >
                  Next
                </button>
              </>
            )}
            
            {step === 2 && (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleBulkOperation}
                  disabled={processing}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {processing ? 'Processing...' : `${operation === 'assign' ? 'Assign' : 'Unassign'} Features`}
                </button>
              </>
            )}
            
            {step === 3 && (
              <button
                onClick={onClose}
                className="btn btn-primary"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkFeatureAssignmentModal 